import React from 'react';
import type { MonthlyBucket } from '../../types';

function fmt(n: number) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(n);
}

function fmtMonth(iso: string) {
  const [year, month] = iso.split('-');
  const d = new Date(Number(year), Number(month) - 1);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short' });
}

interface Props {
  months: MonthlyBucket[];
}

export function MonthlyDataTable({ months }: Props) {
  const totals = months.reduce(
    (acc, m) => ({
      payCount: acc.payCount + m.payment.count,
      payAmount: acc.payAmount + m.payment.totalAmount,
      outCount: acc.outCount + m.payout.count,
      outAmount: acc.outAmount + m.payout.totalAmount,
    }),
    { payCount: 0, payAmount: 0, outCount: 0, outAmount: 0 }
  );

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-3">Monthly Transaction Data</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">Month</th>
              <th className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Payments</th>
              <th className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Payment Vol.</th>
              <th className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Avg Payment</th>
              <th className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Payouts</th>
              <th className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Payout Vol.</th>
              <th className="px-2 sm:px-3 py-2 text-right font-medium text-gray-500 whitespace-nowrap">Avg Payout</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {months.map((m) => (
              <tr key={m.month} className="hover:bg-gray-50">
                <td className="px-2 sm:px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{fmtMonth(m.month)}</td>
                <td className="px-2 sm:px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                  {m.payment.count.toLocaleString()}
                </td>
                <td className="px-2 sm:px-3 py-2 text-right text-gray-700 whitespace-nowrap">{fmt(m.payment.totalAmount)}</td>
                <td className="px-2 sm:px-3 py-2 text-right text-gray-500 whitespace-nowrap">{fmt(m.payment.avgAmount)}</td>
                <td className="px-2 sm:px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                  {m.payout.count.toLocaleString()}
                </td>
                <td className="px-2 sm:px-3 py-2 text-right text-gray-700 whitespace-nowrap">{fmt(m.payout.totalAmount)}</td>
                <td className="px-2 sm:px-3 py-2 text-right text-gray-500 whitespace-nowrap">{fmt(m.payout.avgAmount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 font-medium">
            <tr>
              <td className="px-2 sm:px-3 py-2 text-gray-900 whitespace-nowrap">Totals</td>
              <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">{totals.payCount.toLocaleString()}</td>
              <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">{fmt(totals.payAmount)}</td>
              <td className="px-2 sm:px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                {totals.payCount > 0 ? fmt(totals.payAmount / totals.payCount) : '-'}
              </td>
              <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">{totals.outCount.toLocaleString()}</td>
              <td className="px-2 sm:px-3 py-2 text-right whitespace-nowrap">{fmt(totals.outAmount)}</td>
              <td className="px-2 sm:px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                {totals.outCount > 0 ? fmt(totals.outAmount / totals.outCount) : '-'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
